import { Heading } from '@components/ui/typography/heading';
import { Text } from '@components/ui/typography/text';
import { VStack, Flex } from '@components/ui/layout/flex';
import { ObfuscatedEmail } from '@components/ui/obfuscated-email';

export default function Terms() {
  return (
    <div className="animate-in fade-in duration-300">
      <VStack className="min-h-screen flex-col md:flex-row">
        <Flex className="w-full" align="start" justify="start">
          <div>
            <div>
              <Heading level={2}>Terms of Use</Heading>
            </div>
            <div className="pb-5">
              <Heading level={4} className="py-4 text-gray-800 font-medium">
                Terms of Use
              </Heading>
              <Text className="text-gray-500 text-sm italic font-semibold">
                Effective Date: May 4, 2026
              </Text>
            </div>

            <Text className="text-lg pb-4">
              These Terms of Use (&quot;Terms&quot;) apply to all websites provided by aboboTech
              LLC., doing business as My ClinicAlly (&quot;My ClinicAlly,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;), including the Sites hosted on the domains
              [https://ourforms.co/, https://myclinically.com/, and others] (collectively,
              &quot;Sites&quot;), and all services provided by us in connection with such Sites or
              otherwise, to you (&quot;User,&quot; &quot;Users&quot; &quot;you&quot; or
              &quot;your&quot;), as well as functionality or services we may choose to provide
              through some other mechanism (collectively the &quot;Services&quot;).
            </Text>
            <Text className="text-base pb-4">
              PLEASE READ THESE TERMS OF USE (THE &quot;AGREEMENT&quot;) CAREFULLY BECAUSE THIS
              AGREEMENT CONSTITUTES A LEGALLY BINDING CONTRACT BETWEEN YOU AND MY CLINICALLY. IF
              YOU DO NOT WISH TO ACCEPT THIS AGREEMENT, PLEASE DO NOT ACCESS OR USE THE SERVICES.
              BY ACCESSING, UPLOADING INFORMATION TO, OR OTHERWISE USING, THE SERVICES, YOU AGREE
              (1) TO BE BOUND BY THIS AGREEMENT, (2) YOU HAVE READ AND UNDERSTAND THIS AGREEMENT
              AND (3) YOU ACCEPT THIS AGREEMENT. THIS AGREEMENT CONTAINS AN AGREEMENT TO ARBITRATE
              DISPUTES, WHICH (I) REQUIRES THAT YOU AND MY CLINICALLY ARBITRATE CERTAIN CLAIMS BY
              BINDING, INDIVIDUAL ARBITRATION INSTEAD OF GOING TO COURT; AND (II) LIMITS CLASS
              ACTION CLAIMS.
            </Text>
            <Heading level={6} className="text-black font-extrabold py-6">
              IF YOU ARE EXPERIENCING A MEDICAL EMERGENCY, YOU SHOULD DIAL &quot;911&quot; OR YOUR
              LOCAL EMERGENCY NUMBER IMMEDIATELY.
            </Heading>

            <Text className="text-lg font-extrabold pb-2">
              My ClinicAlly provides services to both healthcare and non-healthcare organizations.
            </Text>

            <Text className="text-lg pb-4">
              My ClinicAlly does not provide medical services or medical advice. My ClinicAlly does
              not make any representations or warranties about the training or skill of any
              healthcare providers who provide services via the Services. You are ultimately
              responsible for choosing your particular healthcare provider, if any. All healthcare
              providers are independent of My ClinicAlly. Any information or advice received from a
              healthcare provider comes from the provider alone, and not from My ClinicAlly. My
              ClinicAlly is not responsible or liable for any advice obtained from a healthcare
              provider or any other user of the Services. You acknowledge that your reliance on any
              information provided by any healthcare provider via the Services is solely at your
              own risk.
            </Text>
            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Services
              </Heading>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">Partners</Text>
              <Text>
                We are a technology company that facilitates your ability to obtain products and
                services sold, offered or furnished by third-party medical providers, facilities or
                partner organizations (&quot;Partners&quot;). The Services may permit you to
                communicate with Partners through a variety of means, including responding to forms
                and direct SMS / email messaging. When you connect with a Partner, the Partner may
                require that you sign consent forms to permit the Partner to communicate with you
                through the Services, or to permit the Partner to provide services to you. We do
                not recommend any particular Partner and we do not guarantee that any particular
                Partner will contact or provide any services to you, even if you contact such
                Partner through the Services.
              </Text>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                We do not practice medicine
              </Text>
              <Text>
                We do not control or take any responsibility for the practice of medicine by any
                healthcare provider, each of whom is solely responsible for the medical care and
                treatment it provides to you. We make no representations or warranties about the
                suitability, reliability, timeliness or accuracy of the medical care and treatment
                provided by any healthcare provider. You acknowledge and agree that we are not a
                healthcare provider and you are not entering into a doctor-patient or other health
                care provider-patient relationship with us.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Eligibility to use the Services
              </Text>
              <Text className="font-bold">
                You may not access or use the Services if you are unwilling to provide a valid
                phone number and/or email address belonging to you, as required for communication
                purposes.. You hereby represent and covenant to us that you satisfy these
                eligibility requirements, you will comply with all applicable laws in visiting,
                accessing, registering with, or using the Services, and you will only use the
                Services for lawful purposes. Our Services are subject to state regulations and may
                change from time to time due to changes in applicable regulatory requirements.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Third Party Components
              </Text>
              <Text>
                The Services may leverage third party services (which may include third party video
                conferencing platforms, third party messaging apps, third party databases, and the
                like) (&quot;Third Party Components&quot;). My ClinicAlly has no responsibility or
                liability for any act, omission, or occurrence that is at all associated with any
                Third Party Component, and your use of any Third Party Component shall be subject
                to the terms and conditions applicable thereto.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Certain Restrictions
              </Text>
              <Text>
                You shall not (i) impersonate any person or entity, or use or provide any
                fraudulent, misleading or inaccurate information; (ii) harass or threaten any
                person; (iii) &quot;frame&quot; or &quot;mirror&quot; any portion of the Services;
                (iv) use any robot, spider, site search/retrieval application or other manual or
                automatic device or process to retrieve, index, &quot;data mine&quot; or in any way
                reproduce or circumvent any aspect of the Services; (vii) harvest or collect
                information about or from Partners or other users of the Services; or (viii) use
                the Services on behalf or for the benefit of any third party.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                User Information
              </Heading>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Privacy Policy
              </Text>
              <Text>
                Our Privacy Policy (&quot;Privacy Policy&quot;), describes our collection, use and
                disclosure of data and information in connection with the Services. We may update
                our Privacy Policy from time to time, in accordance with its terms. Our Privacy
                Policy is incorporated into this Agreement, and by using the Services you agree to
                the collection, use and disclosure practices in our Privacy Policy.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                User Information
              </Text>
              <Text>
                You may provide to My ClinicAlly or to Partners information about yourself in
                connection with your use of the Services (collectively the &quot;User
                Information&quot;). You acknowledge and agree that you are free to share—or not
                share—User Information in connection with your use of the Services, and that to the
                extent you choose to provide User Information to us or to Partners, you do so with
                full understanding of (i) our rights to use such information under this Agreement
                and (ii) the fact that we do not control Partners.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Intellectual Property
              </Heading>
              <Text className="pb-2">
                The Services and all related content, including but not limited to software, text,
                graphics, images, audio, video, logos, trademarks, service marks, trade names, and
                other intellectual property (&quot;Content&quot;) are owned by My ClinicAlly, its
                licensors, or other content providers and are protected by United States and
                international intellectual property laws.
              </Text>

              <Text className="py-2">
                You are granted a limited, non-exclusive, non-transferable, revocable license to
                access and use the Services for their intended purpose.
              </Text>
              <Text className="pt-2">You may not:</Text>
              <ul className="list-disc pl-6 space-y-1">
                <li>Copy, modify, distribute, sell, or lease any part of our Services</li>
                <li>
                  Reverse engineer or attempt to extract source code from our Services
                </li>
                <li>Create derivative works based on the Services</li>
                <li>
                  Use our trademarks, service marks, or branding without written permission
                </li>
                <li>Remove, alter, or obscure any proprietary notices on the Services</li>
              </ul>

              <Text className="py-5">
                All rights not expressly granted are reserved by My ClinicAlly.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Service Modification and Availability
              </Heading>
              <Text>We reserve the right to:</Text>
              <ul className="list-disc pl-6 space-y-1">
                <li>Modify, suspend, or discontinue any part of the Services at any time</li>
                <li>Update these Terms by posting revised Terms</li>
                <li>Impose usage limits or restrict access for security or operational reasons</li>
                <li>Schedule maintenance that may temporarily interrupt Services</li>
              </ul>

              <Text className="py-5">
                We will provide reasonable notice of material changes when practicable, but some
                changes may be implemented immediately for security or legal reasons.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Disclaimers; Limitation of Liability
              </Heading>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                NO WARRANTY
              </Text>
              <Text>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED &quot;AS
                IS&quot; AND &quot;AS AVAILABLE&quot;, AND MY CLINICALLY DISCLAIMS ANY WARRANTIES
                OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT. MY CLINICALLY MAKES NO WARRANTY THAT THE SERVICES WILL BE
                UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                LIMITATION OF LIABILITY
              </Text>
              <Text>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL MY CLINICALLY BE LIABLE
                FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL OR PUNITIVE DAMAGES, OR ANY
                LOSS OF PROFITS OR DATA, ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT OR
                THE SERVICES, EVEN IF MY CLINICALLY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
                DAMAGES. IN NO EVENT SHALL MY CLINICALLY&apos;S TOTAL LIABILITY UNDER THIS
                AGREEMENT EXCEED THE AMOUNT YOU PAID, IF ANY, TO MY CLINICALLY FOR THE USE OF THE
                SERVICES DURING THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE CLAIM.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Indemnity
              </Heading>
              <Text className="text-gray-500 italic font-extrabold">
                You agree to indemnify, defend and hold harmless My ClinicAlly, its affiliates,
                officers, directors, employees, contractors, agents, and representatives from and
                against any and all claims, liabilities, damages, losses, costs, expenses, and fees
                (including reasonable attorneys&apos; fees) arising out of or related to your use
                of the Services, any violation of this Agreement, or any action taken by My
                ClinicAlly as part of its investigation of a suspected violation of this Agreement
                or as a result of its finding or decision that a violation has occurred.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Termination
              </Heading>
              <Text className="text-gray-500 italic font-extrabold">
                My ClinicAlly may terminate or suspend your access to the Services at any time,
                with or without cause, and with or without notice. Upon termination, your right to
                access and use the Services will immediately cease.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Governing Law; Dispute Resolution
              </Heading>
              <Text className="text-gray-500 italic font-extrabold">
                This Agreement, and any dispute between you and us, shall be governed by the laws
                of the State of New York without regard to principles of conflicts of law that
                would result in the application of the law of any other jurisdiction, except that
                the Federal Arbitration Act shall govern the interpretation and enforcement of the
                arbitration provisions set forth below. Unless you and we agree otherwise, in the
                event that Section 10 is found not to apply to you or to a particular claim or
                dispute you agree that any claim or dispute that has arisen or may arise between
                you and us must be resolved exclusively by a state court located in New York
                County, NY or a federal court located in New York County, NY, except that you or we
                are permitted (i) to bring small claims actions in state court in the county in
                which you reside if such court has a small claims procedure; (ii) to bring claims
                for injunctive relief in any court having jurisdiction over the parties; or (iii)
                to seek enforcement of a judgment in any court having jurisdiction over the
                parties. To the extent permitted by law, you and we agree to waive trial by jury in
                any court proceeding.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Miscellaneous
              </Heading>
              <Text className="text-gray-500 italic font-extrabold">
                This Agreement constitutes the entire agreement between you and My ClinicAlly with
                respect to the Services and supersedes all prior or contemporaneous communications
                and proposals, whether electronic, oral or written, between you and My ClinicAlly.
                My ClinicAlly&apos;s failure to enforce any provision of this Agreement shall not
                be deemed a waiver of such provision. If any provision of this Agreement is held to
                be invalid or unenforceable, such provision shall be modified to reflect the
                parties&apos; intention and the remaining provisions of this Agreement shall remain
                in full force and effect.
              </Text>
            </div>

            <div className="pt-5">
              <Text>
                For questions about these Terms of Use, email: <ObfuscatedEmail user="support" />
              </Text>
            </div>
          </div>
        </Flex>
      </VStack>
    </div>
  );
}
